import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, Check, CheckCircle2, Download, Info, Loader2,
  RefreshCw, X, XCircle,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { attachmentDownloadUrl } from "@/api/contracts";
import { downloadAttachment } from "@/api/taskAttachments";
import {
  annexErrorCode, annexErrorDetails, annexGenerate, annexPreview, annexPreviewPdf,
  annexRegistryLookup, annexRequisitesUpdate, fetchAnnexContext,
} from "@/api/annex";
import type {
  AnnexContextResponse, AnnexDocPayload, AnnexGenerateResponse,
  AnnexLookupResponse, AnnexPreviewResponse,
} from "@/api/annex";

/**
 * D-143 C7 — майстер додаткової угоди (ЕДО «Вчасно»). Строго за MOCKUP v3:
 * 4 кроки (майбутні замкнені), 15 станів реєстру (сервер-driven через
 * /annex/registry-lookup), бейджі провенансу (вкл. «· змінено вручну»),
 * guard-діалог назви (Escape НЕ обходить, E8), diff-modal повторного lookup
 * (§7.2: ручні правки мовчки не затираються), confirm-modal дубля номера
 * (409 + Trap-64), стани генерації (Створюємо/успіх/помилка/retry, S4),
 * modal 1000-1200px + sticky chrome + internal scroll + mobile fullscreen.
 *
 * Сервер — власник збірки (P7): крок 4 показує рядки /annex/preview.
 * Код контрагента readonly (§4.3 P1); порожній код → override тільки в
 * документ. Writeback ручних правок — тихий, через write-gate C3
 * (рішення Igor: без чекбокса), ПРОПУСКАЄТЬСЯ в override-режимі.
 */

// ── helpers ──────────────────────────────────────────────────────────────────

const POSITIONS = [
  "Директор", "Генеральний директор", "Виконавчий директор",
  "Керуючий", "Президент", "Уповноважена особа",
];
const BASES = ["статуту", "виписки з ЄДР", "довіреності"];

const reDate = /^(\d{2})\.(\d{2})\.(\d{4})$/;
const reMail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function realDate(s: string): Date | null {
  const m = s.match(reDate);
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  return d.getDate() === +m[1] && d.getMonth() === +m[2] - 1 ? d : null;
}

// input-маска дат (фікс Igor, live-смоук W1): лише цифри, крапки самі після ДД/ММ;
// paste «06.02.2026» і «06022026» дають однаковий результат. Валідація E6 не чіпається.
function maskDate(raw: string): string {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  let out = d.slice(0, 2);
  if (d.length > 2) out += "." + d.slice(2, 4);
  if (d.length > 4) out += "." + d.slice(4, 8);
  return out;
}

function ibanOk(s: string): boolean {
  const v = s.replace(/\s+/g, "").toUpperCase();
  return /^UA\d{2}[0-9A-Z]+$/.test(v) && v.length >= 27 && v.length <= 29;
}

function isoToDdmm(iso: string | null): string {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

function todayDdmm(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return "—";
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
}

function stripFop(name: string): string {
  return name.replace(/^\s*(фізична\s+особа[\s-]*підприємець|ф\.?\s*о\.?\s*п\.?|фоп)\s*/iu, "").trim();
}

function shortPib(full: string): string {
  const w = full.trim().split(/\s+/);
  if (!w[0]) return "";
  let out = w[0];
  for (const i of [1, 2]) if (w[i]) out += ` ${w[i][0]}.`;
  return out;
}

function errMsg(e: unknown): string {
  return (e as Error)?.message ?? "Сталася помилка";
}

// ── provenance badge ─────────────────────────────────────────────────────────

type ProvBase = "реєстр" | "1С" | "вручну" | "з повної назви" | "CRM";

function Badge({ base, edited }: { base: ProvBase; edited?: boolean }) {
  const cls =
    base === "реєстр" || base === "з повної назви"
      ? "bg-accent text-accent-foreground border-accent-foreground/20"
      : base === "вручну"
        ? "bg-warning/20 text-warning-foreground border-warning-foreground/20"
        : "bg-muted text-muted-foreground border-border";
  return (
    <span
      className={cn(
        "rounded border px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap",
        cls,
        edited && "border-dashed",
      )}
    >
      {base}
      {edited ? " · змінено вручну" : ""}
    </span>
  );
}

// ── field wrapper ────────────────────────────────────────────────────────────

function Field({
  label, required, badge, error, hint, children, wide,
}: {
  label: string;
  required?: boolean;
  badge?: React.ReactNode;
  error?: string;
  hint?: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={cn("flex flex-col gap-1", wide && "sm:col-span-2")}>
      <label className="flex flex-wrap items-center gap-2 text-[13px] font-medium">
        {label}
        {required && <span className="text-destructive">*</span>}
        {badge}
      </label>
      {children}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

const inputCls =
  "h-9 w-full rounded-md border bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 read-only:bg-muted read-only:text-muted-foreground";

// ── callout ──────────────────────────────────────────────────────────────────

function Callout({
  tone, icon, children, actions,
}: {
  tone: "ok" | "warn" | "error" | "info";
  icon?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  const cls = {
    ok: "bg-success/10 border-success/40 text-success-foreground",
    warn: "bg-warning/10 border-warning/40 text-warning-foreground",
    error: "bg-destructive/10 border-destructive/40 text-destructive",
    info: "bg-accent border-accent-foreground/20 text-accent-foreground",
  }[tone];
  return (
    <div className={cn("mb-3 flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-[13px]", cls)}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        {children}
        {actions && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}

// ── блок-тексти за blockReason (мокап v3) ────────────────────────────────────

const BLOCK_TEXT: Record<string, string> = {
  dead: "Юрособу припинено або вона в стані припинення — оформити додаткову угоду не можна. Обходу немає.",
  not_found_8: "Юрособу з таким ЄДРПОУ не знайдено. Це не фізособа — це або одрук, або юрособи немає в реєстрі. Перевірте код.",
  not_found_10: "Код не знайдено як ФОП. Імовірно, це фізична особа — додаткові угоди оформлюємо тільки для ТОВ і ФОП.",
  invalid_code: "Код введено невірно (не 8 і не 10 цифр). Код повʼязаний зі стороною договору — виправте його в 1С і дочекайтесь синхронізації.",
  incomplete_unknown: "Реєстр не повернув статус, і клієнт ще не перевірявся. «Невідомо + невідомо» — документ не друкуємо. Спробуйте «Оновити» (force fresh).",
  incomplete_stored_dead: "Реєстр не повернув статус, а збережений статус — «мертвий». Відсутність статусу в реєстрі не «воскрешає» клієнта. Блок.",
  incomplete_name_mismatch: "Реєстр не повернув статус, а назва не збігається з CRM — підтвердження доступне лише при живому статусі з реєстру. Спробуйте «Оновити».",
  stored_dead_manual: "Реєстр недоступний, а збережений статус — «мертвий». Впалий реєстр не обходить гейт. Блок.",
};

// ── main component ───────────────────────────────────────────────────────────

type FormKey =
  | "contractNumber" | "contractDate" | "annexNumber" | "annexDate"
  | "buyerType" | "buyerFullName" | "buyerShortName" | "buyerAddress"
  | "signerName" | "signerPosition" | "signerNameGenitive" | "signerBasis"
  | "poaNumber" | "poaDate" | "iban" | "bankName" | "vatIpn" | "phone"
  | "codeOverride" | "edoEmail";

type Form = Record<FormKey, string>;

const REGISTRY_FIELDS: FormKey[] = ["buyerFullName", "buyerShortName", "buyerAddress", "signerName", "vatIpn"];

export function AnnexWizardModal({
  contractId,
  holdingId,
  onClose,
}: {
  contractId: string;
  holdingId?: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const [ctx, setCtx] = useState<AnnexContextResponse | null>(null);
  const [ctxError, setCtxError] = useState<string | null>(null);

  const [step, setStep] = useState(1);
  const [maxDone, setMaxDone] = useState(0);
  const [dirty, setDirty] = useState(false);

  const [form, setForm] = useState<Form>({
    contractNumber: "", contractDate: "", annexNumber: "", annexDate: todayDdmm(),
    buyerType: "", buyerFullName: "", buyerShortName: "", buyerAddress: "",
    signerName: "", signerPosition: "", signerNameGenitive: "", signerBasis: "статуту",
    poaNumber: "", poaDate: "", iban: "", bankName: "", vatIpn: "", phone: "",
    codeOverride: "", edoEmail: "",
  });
  const [errors, setErrors] = useState<Partial<Record<FormKey, string>>>({});
  const [prov, setProv] = useState<Partial<Record<FormKey, { base: ProvBase; edited: boolean }>>>({});
  const [typeLocked, setTypeLocked] = useState(false);

  const [lookup, setLookup] = useState<AnnexLookupResponse | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [diff, setDiff] = useState<Array<{ key: FormKey; label: string; mine: string; registry: string }> | null>(null);
  const pendingDiffLookup = useRef<AnnexLookupResponse | null>(null);

  const [preview, setPreview] = useState<AnnexPreviewResponse | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // PDF-превʼю (фікс Igor): реальний рендер сервера (.docx→PDF, без БД).
  // Кеш по hash(values) на сесію майстра; будь-який збій (вкл. 503 до image r2)
  // → pdfOff, фолбек = серверні рядки. object-URL-и revoke-аються на unmount.
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfOff, setPdfOff] = useState(false);
  const pdfCache = useRef<Map<string, string>>(new Map());
  const pdfSeq = useRef(0);

  const [genState, setGenState] = useState<"idle" | "busy" | "ok" | "err">("idle");
  const [genResult, setGenResult] = useState<AnnexGenerateResponse | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [dupConfirm, setDupConfirm] = useState<{ annexNumber: string } | null>(null);
  const [closeConfirm, setCloseConfirm] = useState(false);

  // Q3: ключ = сесія майстра; стабільний для retry (подвійний клік/F5-у-межах-сесії)
  const clientRequestId = useMemo(
    () => crypto.randomUUID().replace(/-/g, "").slice(0, 32),
    [],
  );

  const gate = lookup?.gate.mode ?? null;
  const blocked = gate === "blocked";
  const isFop = form.buyerType === "fop";
  const codeSource = lookup?.anchor?.source ?? (ctx?.account.code ? "account" : null);

  // ── context load ──────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    fetchAnnexContext(contractId)
      .then((c) => {
        if (!alive) return;
        setCtx(c);
        const r = c.account.requisites;
        setForm((f) => ({
          ...f,
          contractNumber: c.contract.number,
          contractDate: isoToDdmm(c.contract.signedDate),
          buyerFullName: r.legalNameFull ?? "",
          buyerAddress: r.legalAddress ?? "",
          signerName: r.signerName ?? "",
          signerPosition: r.signerPosition ?? "",
          signerNameGenitive: r.signerNameGenitive ?? "",
          signerBasis: r.signerBasis ?? "статуту",
          iban: r.iban ?? "",
          bankName: r.bankName ?? "",
          vatIpn: r.vatIpn ?? "",
          phone: r.phone ?? "",
        }));
        const crm = (has: string | null): { base: ProvBase; edited: boolean } | undefined =>
          has ? { base: "CRM", edited: false } : undefined;
        setProv({
          buyerFullName: crm(r.legalNameFull),
          buyerAddress: crm(r.legalAddress),
          signerName: crm(r.signerName),
          vatIpn: crm(r.vatIpn),
          phone: r.phone ? { base: "1С", edited: false } : undefined,
        });
      })
      .catch((e) => alive && setCtxError(errMsg(e)));
    return () => { alive = false; };
  }, [contractId]);

  const setField = useCallback((key: FormKey, value: string, fromUser = true) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (fromUser) {
      setDirty(true);
      setProv((p) => {
        const cur = p[key];
        if (!cur) return { ...p, [key]: { base: "вручну", edited: false } };
        return { ...p, [key]: { ...cur, edited: cur.base !== "вручну" } };
      });
      setErrors((e) => ({ ...e, [key]: undefined }));
    }
  }, []);

  // ── apply lookup result to form ───────────────────────────────────────────
  const applyLookup = useCallback((r: AnnexLookupResponse, opts?: { keepMine?: boolean }) => {
    setLookup(r);
    if (!r.registry) return;
    const reg = r.registry;
    const type = reg.type === "fop" ? "fop" : reg.type === "company" ? "company" : "";
    const next: Array<[FormKey, string | null, ProvBase]> = [
      ["buyerFullName", reg.fullName, "реєстр"],
      ["buyerAddress", reg.location, "реєстр"],
      ["vatIpn", reg.vatCode, "реєстр"],
    ];
    if (type === "fop") {
      next.push(["signerName", reg.ceoName ?? reg.fullName, reg.ceoName ? "реєстр" : "з повної назви"]);
      next.push(["buyerShortName", reg.fullName ? `ФОП ${shortPib(stripFop(reg.fullName))}` : null, "з повної назви"]);
    } else {
      next.push(["signerName", reg.ceoName, "реєстр"]);
      next.push(["buyerShortName", reg.shortName ?? reg.fullName, "реєстр"]);
    }

    setForm((f) => {
      const out = { ...f };
      for (const [key, value] of next) {
        if (value == null || value === "") continue;
        if (opts?.keepMine && prov[key]?.edited && f[key] && f[key] !== value) continue;
        out[key] = value;
      }
      if (type) {
        out.buyerType = type;
        // фікс Igor: дефолт «Діє на підставі» слідує за типом — ФОП не «на
        // підставі статуту». Свідомий вибір (довіреності / CRM-значення) не чіпаємо.
        if (type === "fop" && out.signerBasis === "статуту") out.signerBasis = "виписки з ЄДР";
        if (type === "company" && out.signerBasis === "виписки з ЄДР") out.signerBasis = "статуту";
      }
      return out;
    });
    setProv((p) => {
      const out = { ...p };
      for (const [key, value, base] of next) {
        if (value == null || value === "") {
          if (!out[key]) out[key] = { base: "вручну", edited: false };
          continue;
        }
        if (opts?.keepMine && out[key]?.edited) continue;
        out[key] = { base, edited: false };
      }
      return out;
    });
    if (type) setTypeLocked(r.gate.mode === "ok" || r.gate.mode === "ok_stored");
  }, [prov]);

  // ── lookup ────────────────────────────────────────────────────────────────
  const runLookup = useCallback(async (opts: { forceFresh?: boolean; confirm?: boolean } = {}) => {
    setLookupBusy(true);
    setLookupError(null);
    try {
      const r = await annexRegistryLookup(contractId, {
        forceFresh: opts.forceFresh,
        confirmNameMismatch: opts.confirm,
        codeOverride: form.codeOverride || undefined,
      });
      // §7.2: повторний lookup не затирає ручну правку мовчки → diff-modal
      if (opts.forceFresh && r.registry && (r.gate.mode === "ok" || r.gate.mode === "ok_stored")) {
        const labels: Partial<Record<FormKey, string>> = {
          buyerFullName: "Повна назва", buyerShortName: "Коротка назва",
          buyerAddress: "Юридична адреса", signerName: isFop ? "ПІБ ФОП" : "Керівник (ПІБ)",
          vatIpn: "ІПН платника ПДВ",
        };
        const regValue: Partial<Record<FormKey, string | null>> = {
          buyerFullName: r.registry.fullName,
          buyerShortName: r.registry.type === "fop"
            ? (r.registry.fullName ? `ФОП ${shortPib(stripFop(r.registry.fullName))}` : null)
            : (r.registry.shortName ?? r.registry.fullName),
          buyerAddress: r.registry.location,
          signerName: r.registry.ceoName ?? (r.registry.type === "fop" ? r.registry.fullName : null),
          vatIpn: r.registry.vatCode,
        };
        const diffs: Array<{ key: FormKey; label: string; mine: string; registry: string }> = [];
        for (const key of REGISTRY_FIELDS) {
          const rv = regValue[key];
          if (rv && prov[key]?.edited && form[key] && form[key] !== rv) {
            diffs.push({ key, label: labels[key] ?? key, mine: form[key], registry: rv });
          }
        }
        if (diffs.length > 0) {
          pendingDiffLookup.current = r;
          setDiff(diffs);
          setLookup(r); // стан/плашка оновлюються; значення — після рішення
          return;
        }
      }
      applyLookup(r);
    } catch (e) {
      setLookupError(errMsg(e));
    } finally {
      setLookupBusy(false);
    }
  }, [contractId, form, prov, isFop, applyLookup]);

  // авто-lookup при вході на крок 2 (код є на картці → lookup автоматичний, §4.3)
  const autoLookupDone = useRef(false);
  useEffect(() => {
    if (step === 2 && !autoLookupDone.current && ctx && ctx.account.code) {
      autoLookupDone.current = true;
      void runLookup();
    }
  }, [step, ctx, runLookup]);

  // ── client-side validation (UX-дзеркало; сервер авторитетний E6) ─────────
  const validateStep = useCallback((s: number): boolean => {
    const e: Partial<Record<FormKey, string>> = {};
    const req = (k: FormKey, msg = "Обовʼязкове поле") => {
      if (!form[k].trim()) e[k] = msg;
    };
    if (s === 1) {
      req("contractNumber"); req("contractDate"); req("annexNumber"); req("annexDate");
      for (const k of ["contractDate", "annexDate"] as FormKey[]) {
        if (form[k].trim() && !realDate(form[k])) e[k] = "Невірна дата (ДД.ММ.РРРР, реальна)";
      }
      const c = realDate(form.contractDate);
      const a = realDate(form.annexDate);
      if (c && a && a < c) e.annexDate = "Дата угоди не може бути раніше дати договору";
    }
    if (s === 2) {
      if (form.buyerType !== "company" && form.buyerType !== "fop") {
        e.buyerType = "Оберіть тип — без нього преамбула юридично неправильна";
      }
      req("buyerFullName"); req("buyerAddress");
      if (!isFop) {
        req("buyerShortName"); req("signerName"); req("signerPosition");
        req("signerNameGenitive", "Вкажіть ПІБ у родовому відмінку");
      }
      req("signerBasis");
      if (form.signerBasis === "довіреності") {
        req("poaNumber");
        req("poaDate");
        if (form.poaDate.trim() && !realDate(form.poaDate)) e.poaDate = "Невірна дата (ДД.ММ.РРРР, реальна)";
      }
      if (form.iban.trim() && !ibanOk(form.iban)) e.iban = "IBAN: UA + 27-29 знаків";
    }
    if (s === 3) {
      req("edoEmail");
      if (form.edoEmail.trim() && !reMail.test(form.edoEmail.trim())) e.edoEmail = "Невірний формат email";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form, isFop]);

  // ── payload / preview / generate ─────────────────────────────────────────
  const buildPayload = useCallback((extra?: Partial<AnnexDocPayload>): AnnexDocPayload => ({
    annexNumber: form.annexNumber.trim(),
    annexDate: form.annexDate.trim(),
    contractNumber: form.contractNumber.trim(),
    contractDate: form.contractDate.trim(),
    buyerType: (form.buyerType || "") as AnnexDocPayload["buyerType"],
    buyerFullName: form.buyerFullName.trim(),
    buyerShortName: form.buyerShortName.trim(),
    buyerAddress: form.buyerAddress.trim(),
    signerName: form.signerName.trim(),
    signerPosition: form.signerPosition.trim(),
    signerNameGenitive: form.signerNameGenitive.trim(),
    signerBasis: form.signerBasis.trim(),
    poaNumber: form.poaNumber.trim(),
    poaDate: form.poaDate.trim(),
    iban: form.iban.trim(),
    bankName: form.bankName.trim(),
    vatIpn: form.vatIpn.trim(),
    phone: form.phone.trim(),
    edrpou: (lookup?.anchor?.code ?? ctx?.account.code ?? form.codeOverride).trim(),
    edoEmail: form.edoEmail.trim(),
    ...extra,
  }), [form, lookup, ctx]);

  const loadPreview = useCallback(async () => {
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      setPreview(await annexPreview(contractId, buildPayload()));
    } catch (e) {
      const details = annexErrorDetails(e);
      const fields = details.fields as Record<string, string> | undefined;
      if (annexErrorCode(e) === "validation" && fields) {
        setErrors((prev) => ({ ...prev, ...fields }));
        setPreviewError("Сервер відхилив дані: " + Object.values(fields).join(" · "));
      } else {
        setPreviewError(errMsg(e));
      }
      setPreview(null);
    } finally {
      setPreviewBusy(false);
    }
  }, [contractId, buildPayload]);

  useEffect(() => {
    if (step === 4 && genState === "idle") void loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ── PDF-превʼю: debounce на зміну даних + кеш у межах сесії майстра ───────
  const payloadKey = useMemo(() => JSON.stringify(buildPayload()), [buildPayload]);

  useEffect(() => {
    if (step !== 4 || genState !== "idle" || pdfOff) return;
    const cached = pdfCache.current.get(payloadKey);
    if (cached) {
      setPdfUrl(cached);
      return;
    }
    const seq = ++pdfSeq.current;
    setPdfBusy(true);
    setPdfUrl(null);
    const t = setTimeout(() => {
      annexPreviewPdf(contractId, buildPayload())
        .then((blob) => {
          if (seq !== pdfSeq.current) return;
          const url = URL.createObjectURL(blob);
          pdfCache.current.set(payloadKey, url);
          setPdfUrl(url);
          setPdfBusy(false);
        })
        .catch(() => {
          if (seq !== pdfSeq.current) return;
          // 503 pdf_preview_unavailable (немає soffice до image r2) чи збій
          // конвертації — тихий фолбек на серверні рядки; validation покаже loadPreview
          setPdfOff(true);
          setPdfBusy(false);
        });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, payloadKey, genState, pdfOff]);

  useEffect(() => () => {
    for (const url of pdfCache.current.values()) URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    pdfCache.current.clear();
  }, []);

  const doGenerate = useCallback(async (confirmDuplicate = false) => {
    setGenState("busy");
    setGenError(null);
    try {
      // тихий writeback ручних правок через write-gate (Igor: без чекбокса);
      // в override-режимі реквізити чужого коду в Account НЕ пишуться (§4.3)
      if (codeSource === "account") {
        const fields: Record<string, string | null> = {
          cCLegalNameFull: form.buyerFullName.trim() || null,
          cCLegalAddress: form.buyerAddress.trim() || null,
          cCSignerName: form.signerName.trim() || null,
          cCSignerBasis: form.signerBasis.trim() || null,
          cCSignerNameGenitive: form.signerNameGenitive.trim() || null,
          cCBankAccount: form.iban.trim() || null,
          cCBankName: form.bankName.trim() || null,
          cCIpn: form.vatIpn.trim() || null,
          cCPrimaryPhone: form.phone.trim() || null,
        };
        if (!isFop && form.signerPosition.trim()) fields.cCSignerPosition = form.signerPosition.trim();
        await annexRequisitesUpdate(contractId, fields);
      }

      const r = await annexGenerate(contractId, buildPayload({
        clientRequestId,
        confirmDuplicateAnnexNumber: confirmDuplicate,
      }));
      setGenResult(r);
      setGenState("ok");
      // Post-write refresh (СТАНДАРТ Day 30)
      void qc.invalidateQueries({ queryKey: ["contracts", "detail", contractId] });
      void qc.invalidateQueries({ queryKey: ["contracts", "list"] });
    } catch (e) {
      const code = annexErrorCode(e);
      if (code === "duplicate_annex_number") {
        setGenState("idle");
        setDupConfirm({ annexNumber: form.annexNumber });
        return;
      }
      if (code === "validation") {
        const fields = annexErrorDetails(e).fields as Record<string, string> | undefined;
        if (fields) setErrors((prev) => ({ ...prev, ...fields }));
      }
      setGenError(errMsg(e));
      setGenState("err");
    }
  }, [contractId, form, isFop, codeSource, buildPayload, clientRequestId, qc]);

  // ── navigation ────────────────────────────────────────────────────────────
  const goNext = () => {
    if (step === 4) {
      const existing = ctx?.existingAnnexNumbers ?? [];
      if (existing.includes(form.annexNumber.trim())) {
        setDupConfirm({ annexNumber: form.annexNumber.trim() });
        return;
      }
      void doGenerate(false);
      return;
    }
    if (!validateStep(step)) return;
    setMaxDone((m) => Math.max(m, step));
    setStep(step + 1);
  };

  const canGoTo = (i: number) =>
    i <= maxDone + 1 && !(blocked && i > 2) && genState !== "ok" && genState !== "busy";

  const tryClose = useCallback(() => {
    if (genState === "busy") return;
    if (dirty && genState !== "ok") setCloseConfirm(true);
    else onClose();
  }, [dirty, genState, onClose]);

  // Escape: закриває майстер (з guard'ом «дані буде втрачено»), але НЕ діалоги
  // guard/diff/dup — вони вирішуються лише кнопками (E8).
  const guardActive = gate === "pending_confirmation" || diff !== null || dupConfirm !== null;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (guardActive || closeConfirm) return; // E8: Escape вимкнено на діалогах
      tryClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [guardActive, closeConfirm, tryClose]);

  const fetched = lookup?.registry?.fetchedAt ?? lookup?.fetchedAt;
  const storedVerifiedAt = lookup?.stored?.verifiedAt ?? ctx?.account.registryStatusVerifiedAt ?? null;

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-0 sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Створити додаткову угоду"
        className="flex h-full max-h-full w-full flex-col overflow-hidden border bg-background shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-72px)] sm:w-full sm:max-w-[1150px] sm:rounded-xl"
      >
        {/* sticky header */}
        <div className="flex flex-none items-center justify-between gap-4 border-b px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">Створити додаткову угоду</h2>
            <p className="truncate text-xs text-muted-foreground">
              {ctx ? `${ctx.account.legalName} · ${ctx.contract.name1c || "Договір"}${ctx.contract.signedDate ? ` · від ${isoToDdmm(ctx.contract.signedDate)}` : ""}` : "…"}
            </p>
          </div>
          <button
            type="button"
            aria-label="Закрити"
            onClick={tryClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* sticky steps */}
        <div className="flex flex-none gap-6 overflow-x-auto border-b px-4 sm:px-6">
          {["Договір", "Покупець", "Дані угоди", "Перевірка"].map((label, idx) => {
            const i = idx + 1;
            const unlocked = canGoTo(i);
            return (
              <button
                key={label}
                type="button"
                disabled={!unlocked && i !== step}
                onClick={() => {
                  if (!canGoTo(i)) return;
                  if (i > step && !validateStep(step)) return;
                  setStep(i);
                }}
                className={cn(
                  "flex items-center gap-2 whitespace-nowrap border-b-2 border-transparent py-2.5 text-[13px]",
                  i === step
                    ? "border-primary font-semibold text-foreground"
                    : unlocked
                      ? "text-muted-foreground hover:text-foreground"
                      : "cursor-not-allowed text-muted-foreground/50",
                )}
              >
                <span
                  className={cn(
                    "grid h-5 w-5 place-items-center rounded-full text-[11px] font-semibold",
                    i === step
                      ? "bg-primary text-primary-foreground"
                      : i <= maxDone
                        ? "bg-success/20 text-success-foreground"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {i <= maxDone && i !== step ? <Check className="h-3 w-3" /> : i}
                </span>
                {label}
                {!unlocked && i !== step && <span className="text-[10px]">🔒</span>}
              </button>
            );
          })}
        </div>

        {/* body — internal scroll */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {ctxError && (
            <Callout tone="error" icon={<XCircle className="h-4 w-4" />}>
              {ctxError.includes("привʼяжіть") || ctxError.includes("no_account")
                ? "Спочатку привʼяжіть юридичну особу в картці договору."
                : ctxError}
            </Callout>
          )}

          {/* STEP 1 */}
          {step === 1 && ctx && (
            <div>
              <Callout tone="warn" icon={<AlertTriangle className="h-4 w-4" />}>
                <b>Номер і дата — підказка з 1С, перевірте.</b> Номер заповнено лише у 4% договорів.
                Виправлення діє <b>тільки для цього документа</b>.
              </Callout>
              <h4 className="mb-2.5 mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Договір</h4>
              <div className="grid gap-3.5 sm:grid-cols-2">
                <Field label="Клієнт" hint="З картки договору. Не змінюється">
                  <input className={inputCls} readOnly value={ctx.account.legalName} />
                </Field>
                <Field label="Назва в 1С" hint="У документ не потрапляє">
                  <input className={inputCls} readOnly value={ctx.contract.name1c} />
                </Field>
                <Field label="№ договору" required error={errors.contractNumber}
                  badge={<Badge base="1С" edited={prov.contractNumber?.edited} />}>
                  <input className={inputCls} value={form.contractNumber}
                    onChange={(e) => { setField("contractNumber", e.target.value); setProv((p) => ({ ...p, contractNumber: { base: "1С", edited: true } })); }} />
                </Field>
                <Field label="Дата договору" required error={errors.contractDate}
                  hint={realDate(form.contractDate) ? `У документі: буде «${form.contractDate.slice(0, 2)}» + місяць прописом` : undefined}
                  badge={<Badge base="1С" edited={prov.contractDate?.edited} />}>
                  <input className={cn(inputCls, "vdate")} placeholder="ДД.ММ.РРРР" inputMode="numeric" maxLength={10} value={form.contractDate}
                    onChange={(e) => { setField("contractDate", maskDate(e.target.value)); setProv((p) => ({ ...p, contractDate: { base: "1С", edited: true } })); }} />
                </Field>
              </div>
              <h4 className="mb-2.5 mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Угода</h4>
              <div className="grid gap-3.5 sm:grid-cols-2">
                <Field label="№ додаткової угоди" required error={errors.annexNumber}
                  badge={<Badge base="вручну" />}
                  hint={ctx.existingAnnexNumbers.includes(form.annexNumber.trim())
                    ? `⚠ Угода №${form.annexNumber.trim()} до цього договору вже є в CRM — при створенні попросимо підтвердження.`
                    : undefined}>
                  <input className={inputCls} value={form.annexNumber}
                    onChange={(e) => setField("annexNumber", e.target.value)} />
                </Field>
                <Field label="Дата угоди" required error={errors.annexDate}>
                  <input className={cn(inputCls, "vdate")} placeholder="ДД.ММ.РРРР" inputMode="numeric" maxLength={10} value={form.annexDate}
                    onChange={(e) => setField("annexDate", maskDate(e.target.value))} />
                </Field>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && ctx && (
            <div>
              <div className="mb-3 flex flex-wrap items-end gap-2.5">
                <Field label="Код ЄДРПОУ / ІПН"
                  badge={<Badge base={ctx.account.code ? "1С" : "вручну"} />}
                  hint={ctx.account.code
                    ? "Код клієнта з картки — змінити контрагента в майстрі неможливо."
                    : "Код не заповнений — впишіть для цього документа. Щоб зберегти назавжди — виправте в 1С."}>
                  <input
                    className={cn(inputCls, "w-48 font-mono")}
                    readOnly={!!ctx.account.code}
                    value={ctx.account.code || form.codeOverride}
                    onChange={(e) => setField("codeOverride", e.target.value)}
                  />
                </Field>
                <Button type="button" variant="outline" className="h-9 gap-1.5"
                  disabled={lookupBusy}
                  onClick={() => void runLookup()}>
                  {lookupBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Підтягнути з реєстру
                </Button>
              </div>

              {lookupError && (
                <Callout tone="error" icon={<XCircle className="h-4 w-4" />}>{lookupError}</Callout>
              )}

              {/* стан гейта */}
              {lookup && gate === "ok" && (
                <Callout tone="ok" icon={<CheckCircle2 className="h-4 w-4" />}>
                  <b>Зареєстровано.</b> Дані з реєстру{lookup.fromCache ? " (кеш HUB)" : ""}.
                  {isFop && (
                    <> ФОП: коротка назва і підпис зібрані з ПІБ (реєстр shortName/ceoName для ФОП не дає). Преамбула без «в особі».</>
                  )}
                </Callout>
              )}
              {lookup && gate === "ok_stored" && (
                <Callout tone="warn" icon={<AlertTriangle className="h-4 w-4" />}>
                  <b>Реєстр не повернув статус (found_incomplete).</b> Використано збережений
                  «зареєстровано» від <b>{fmtDateTime(storedVerifiedAt)}</b> (реквізити свіжі
                  — {fmtDateTime(fetched)}). Сумніваєтесь — «Оновити».
                </Callout>
              )}
              {lookup && gate === "manual" && (
                <Callout tone="warn" icon={<AlertTriangle className="h-4 w-4" />}>
                  <b>{lookup.state === "quota_exhausted" ? "Квоту реєстру вичерпано" : "Реєстр недоступний"} — ручний режим.</b>{" "}
                  {lookup.recoversAt ? <>Відновиться {fmtDateTime(lookup.recoversAt)}. </> : null}
                  {lookup.stored?.status === "active"
                    ? <>Збережений статус «зареєстровано» від {fmtDateTime(storedVerifiedAt)} → генерація дозволена. </>
                    : <>Збереженого статусу немає — ручний режим працює (перший документ клієнта не блокується); прапорець «підтверджено» НЕ знімається. </>}
                  Оберіть тип і заповніть реквізити.
                </Callout>
              )}
              {lookup && blocked && (
                <Callout tone="error" icon={<XCircle className="h-4 w-4" />}
                  actions={
                    <>
                      {holdingId && (
                        <Button type="button" size="sm" variant="outline"
                          onClick={() => window.open(`/clients/${holdingId}`, "_blank")}>
                          Відкрити картку клієнта
                        </Button>
                      )}
                      {(lookup.gate.blockReason === "incomplete_unknown" || lookup.gate.blockReason === "incomplete_name_mismatch") && (
                        <Button type="button" size="sm" variant="outline" disabled={lookupBusy}
                          onClick={() => void runLookup({ forceFresh: true })}>
                          ↻ Оновити
                        </Button>
                      )}
                    </>
                  }>
                  <b>Оформити не можна.</b>{" "}
                  {BLOCK_TEXT[lookup.gate.blockReason ?? ""] ?? "Генерація заблокована гейтом реєстру."}
                </Callout>
              )}

              {/* поля — приховані при блоці та pending_confirmation (E8) */}
              {(!lookup || gate === "ok" || gate === "ok_stored" || gate === "manual") && (
                <>
                  {lookup && (gate === "ok" || gate === "ok_stored") && (
                    <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      <span>
                        Реквізити від <b className="text-foreground">{fmtDateTime(fetched)}</b>
                        {" · "}статус підтверджено <b className="text-foreground">{fmtDateTime(gate === "ok" ? fetched : storedVerifiedAt)}</b>
                        {" · "}через HUB
                      </span>
                      <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
                        disabled={lookupBusy}
                        onClick={() => void runLookup({ forceFresh: true })}>
                        ↻ Оновити (force fresh)
                      </Button>
                    </div>
                  )}

                  <h4 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Тип покупця{" "}
                    <span className="normal-case font-normal">
                      {typeLocked ? "— підтверджено реєстром" : "— оберіть вручну"}
                    </span>
                  </h4>
                  <div className="grid gap-3.5 sm:grid-cols-2">
                    <Field label="Тип" required error={errors.buyerType}>
                      <select className={inputCls} value={form.buyerType} disabled={typeLocked}
                        onChange={(e) => {
                          const type = e.target.value;
                          setField("buyerType", type);
                          // фікс Igor: дефолт basis перемикається разом із типом
                          if (type === "fop" && form.signerBasis === "статуту") setField("signerBasis", "виписки з ЄДР", false);
                          if (type === "company" && form.signerBasis === "виписки з ЄДР") setField("signerBasis", "статуту", false);
                        }}>
                        <option value="">Не впевнений</option>
                        <option value="company">Юрособа (ТОВ/ПП/…)</option>
                        <option value="fop">ФОП</option>
                      </select>
                    </Field>
                  </div>

                  <h4 className="mb-2.5 mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Реквізити</h4>
                  <div className="grid gap-3.5 sm:grid-cols-2">
                    <Field wide label="Повна назва" required error={errors.buyerFullName}
                      badge={prov.buyerFullName && <Badge {...prov.buyerFullName} />}>
                      <input className={inputCls} value={form.buyerFullName}
                        onChange={(e) => setField("buyerFullName", e.target.value)} />
                    </Field>
                    <Field label="Коротка назва" required={!isFop} error={errors.buyerShortName}
                      badge={prov.buyerShortName && <Badge {...prov.buyerShortName} />}
                      hint={isFop ? "Зібрано сервером із ПІБ" : undefined}>
                      <input className={inputCls} value={form.buyerShortName}
                        onChange={(e) => setField("buyerShortName", e.target.value)} />
                    </Field>
                    <Field label="Телефон" badge={prov.phone && <Badge {...prov.phone} />}>
                      <input className={inputCls} value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)} />
                    </Field>
                    <Field wide label="Юридична адреса" required error={errors.buyerAddress}
                      badge={prov.buyerAddress && <Badge {...prov.buyerAddress} />}>
                      <input className={inputCls} value={form.buyerAddress}
                        onChange={(e) => setField("buyerAddress", e.target.value)} />
                    </Field>
                  </div>

                  <h4 className="mb-2.5 mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Хто підписує</h4>
                  <div className="grid gap-3.5 sm:grid-cols-2">
                    {!isFop && (
                      <Field label="Посада" required error={errors.signerPosition}
                        badge={<Badge base="вручну" />} hint="Закритий список — вільний текст у документ не потрапляє">
                        <select className={inputCls} value={form.signerPosition}
                          onChange={(e) => setField("signerPosition", e.target.value)}>
                          <option value="">—</option>
                          {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </Field>
                    )}
                    <Field label={isFop ? "ПІБ ФОП" : "Керівник (ПІБ)"} required error={errors.signerName}
                      badge={prov.signerName && <Badge {...prov.signerName} />}
                      hint={isFop ? "Реєстр ceoName для ФОП не дає" : undefined}>
                      <input className={inputCls} value={form.signerName}
                        onChange={(e) => setField("signerName", e.target.value)} />
                    </Field>
                    {!isFop && (
                      <Field wide label="У родовому відмінку" required error={errors.signerNameGenitive}
                        badge={<Badge base="вручну" />}
                        hint="W1: вручну. Пізніше AI підставлятиме — ви перевіряєте закінчення.">
                        <input className={inputCls} placeholder="Кузнєцова Олександра Ілларіоновича"
                          value={form.signerNameGenitive}
                          onChange={(e) => setField("signerNameGenitive", e.target.value)} />
                      </Field>
                    )}
                    <Field label="Діє на підставі" required error={errors.signerBasis} badge={<Badge base="вручну" />}>
                      <select className={inputCls} value={form.signerBasis}
                        onChange={(e) => setField("signerBasis", e.target.value)}>
                        {BASES.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </Field>
                    {form.signerBasis === "довіреності" && (
                      <>
                        <Field label="№ довіреності" required error={errors.poaNumber}>
                          <input className={inputCls} placeholder="№ 5" value={form.poaNumber}
                            onChange={(e) => setField("poaNumber", e.target.value)} />
                        </Field>
                        <Field label="Дата довіреності" required error={errors.poaDate}>
                          <input className={cn(inputCls, "vdate")} placeholder="01.02.2026" inputMode="numeric" maxLength={10} value={form.poaDate}
                            onChange={(e) => setField("poaDate", maskDate(e.target.value))} />
                        </Field>
                      </>
                    )}
                  </div>

                  <h4 className="mb-2.5 mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Банк і податки{" "}
                    <span className="normal-case font-normal">— порожнє = порожній рядок у документі (рівні тримаються)</span>
                  </h4>
                  <div className="grid gap-3.5 sm:grid-cols-2">
                    <Field label="IBAN" error={errors.iban} badge={<Badge base="вручну" />}>
                      <input className={cn(inputCls, "font-mono")} placeholder="UA00 …" value={form.iban}
                        onChange={(e) => setField("iban", e.target.value)} />
                    </Field>
                    <Field label="Банк" badge={<Badge base="вручну" />}>
                      <input className={inputCls} placeholder="в АТ «…», МФО …" value={form.bankName}
                        onChange={(e) => setField("bankName", e.target.value)} />
                    </Field>
                    <Field label="ІПН платника ПДВ" badge={prov.vatIpn && <Badge {...prov.vatIpn} />}
                      hint={form.vatIpn ? "Платник ПДВ" : "Порожньо = не платник АБО реєстр не дав — впишіть, якщо клієнт платник"}>
                      <input className={cn(inputCls, "font-mono")} value={form.vatIpn}
                        onChange={(e) => setField("vatIpn", e.target.value)} />
                    </Field>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div>
              <Callout tone="info" icon={<Info className="h-4 w-4" />}>
                Поля цього кроку — від шаблону <b>«Додаткова угода · ЕДО Вчасно»</b>.
              </Callout>
              <h4 className="mb-2.5 mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Пункт 3.7 — електронний документообіг
              </h4>
              <div className="grid gap-3.5 sm:grid-cols-2">
                <Field label="Постачальник">
                  <input className={inputCls} readOnly value="info@kormaxtrade.com.ua" />
                </Field>
                <Field label="Email покупця для «Вчасно»" required error={errors.edoEmail} badge={<Badge base="вручну" />}>
                  <input className={inputCls} type="email" value={form.edoEmail}
                    onChange={(e) => setField("edoEmail", e.target.value)} />
                </Field>
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div>
              {genState === "idle" && (
                <>
                  <Callout tone="info" icon={<Info className="h-4 w-4" />}>
                    {!pdfOff ? (
                      <><b>Так виглядатиме документ.</b> Рендер реальний — сервер зібрав файл і показує його як PDF.</>
                    ) : (
                      <><b>Попередній перегляд змісту.</b> Рядки зібрав сервер (/annex/preview) — рівно вони підуть у файл.</>
                    )}
                  </Callout>
                  {(previewBusy || (!pdfOff && pdfBusy)) && (
                    <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Готуємо перегляд…
                    </div>
                  )}
                  {previewError && (
                    <Callout tone="error" icon={<XCircle className="h-4 w-4" />}>{previewError}</Callout>
                  )}
                  {!pdfOff && pdfUrl && !pdfBusy && !previewError && (
                    <>
                      <iframe
                        src={pdfUrl}
                        title="Так виглядатиме документ"
                        className="w-full rounded-lg border bg-card"
                        style={{ height: "min(62vh, 760px)" }}
                      />
                      {preview && preview.missing.length > 0 && (
                        <p className="mt-2.5 text-xs text-muted-foreground">
                          Не заповнено: {preview.missing.join(", ")} — рядки будуть порожні, рівні збережуться.
                        </p>
                      )}
                    </>
                  )}
                  {pdfOff && preview && !previewBusy && (
                    <>
                      <div className="rounded-lg border bg-card p-5 text-[12.5px] leading-relaxed" style={{ fontFamily: "Arial, sans-serif" }}>
                        <div className="mb-2 text-right text-[11px] text-muted-foreground" style={{ fontFamily: "inherit" }}>◇ зібрано сервером</div>
                        <div className="text-center font-bold">ДОДАТКОВА УГОДА № <mark className="rounded-sm bg-warning/30 px-0.5">{preview.lines.annex_number}</mark></div>
                        <div className="mb-3 text-center">
                          до ДОГОВОРУ ПОСТАВКИ № <mark className="rounded-sm bg-warning/30 px-0.5">{preview.lines.contract_number}</mark>{" "}
                          від <mark className="rounded-sm bg-warning/30 px-0.5">{preview.lines.contract_date}</mark>
                        </div>
                        <p className="mb-2 flex justify-between">
                          <span>м. Київ</span>
                          <mark className="rounded-sm bg-warning/30 px-0.5">{preview.lines.annex_date}</mark>
                        </p>
                        <p className="mb-2 text-justify">
                          … та <b><mark className="rounded-sm bg-warning/30 px-0.5">{preview.lines.buyer_full_name}</mark></b>{" "}
                          (надалі іменується «Покупець»), <mark className="rounded-sm bg-warning/30 px-0.5">{preview.lines.buyer_acting_clause}</mark>, з іншої сторони …
                        </p>
                        <p className="mb-2 pl-6">3.7. … Покупець: <mark className="rounded-sm bg-warning/30 px-0.5">{preview.lines.buyer_edo_email}</mark></p>
                        <div className="mt-2 grid gap-6 border-t pt-2 sm:grid-cols-2">
                          <div className="whitespace-pre-line">
                            <b>ПОСТАЧАЛЬНИК:</b>{"\n"}<b>ТОВ «КОРМАКС ТРЕЙД»</b>{"\n"}
                            {"Код ЄДРПОУ 40891407\nАдреса: Україна, 04050, м.Київ\nIBAN: UA 593052990000026009005034785\nв ФІЛІЯ \"РОЗРАХ.ЦЕНТР\" АТ КБ\"ПРИВАТБАНК\"\nІПН 408914026597\n+380 44 392-21-82"}
                          </div>
                          <div className="whitespace-pre-line">
                            <b>ПОКУПЕЦЬ:</b>{"\n"}<b><mark className="rounded-sm bg-warning/30 px-0.5">{preview.lines.buyer_name}</mark></b>{"\n"}
                            <mark className="rounded-sm bg-warning/30 px-0.5 whitespace-pre-line">{preview.lines.buyer_requisites}</mark>
                          </div>
                        </div>
                        <div className="mt-3 grid gap-6 sm:grid-cols-2">
                          <div>Директор _________  Плуженко І. Ф.</div>
                          <div>
                            <mark className="rounded-sm bg-warning/30 px-0.5">
                              {preview.lines.signer_position} _________ {preview.lines.signer_short}
                            </mark>
                          </div>
                        </div>
                      </div>
                      {preview.missing.length > 0 && (
                        <p className="mt-2.5 text-xs text-muted-foreground">
                          Не заповнено: {preview.missing.join(", ")} — рядки будуть порожні, рівні збережуться.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}

              {genState === "busy" && (
                <div className="py-12 text-center">
                  <Loader2 className="mx-auto mb-3 h-7 w-7 animate-spin text-primary" />
                  <p className="font-semibold">Створюємо документ…</p>
                  <p className="mt-1 text-xs text-muted-foreground">Ідемпотентний запит — повторний клік не створить дубль.</p>
                </div>
              )}

              {genState === "ok" && genResult && (
                <div className="py-10 text-center">
                  <div className="mb-2 text-4xl">✅</div>
                  <p className="font-semibold">Додаткова угода №{genResult.annexNumber} створена</p>
                  <p className="mx-auto mt-1.5 mb-4 max-w-md text-xs text-muted-foreground">
                    Запис зʼявився у «Документи» договору. Файл незмінний; нові значення = нова генерація.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button type="button" className="gap-1.5"
                      onClick={() => void downloadAttachment(
                        attachmentDownloadUrl(contractId, genResult.attachmentId),
                        genResult.fileName,
                      )}>
                      <Download className="h-3.5 w-3.5" /> Завантажити DOCX
                    </Button>
                    <Button type="button" variant="outline" onClick={onClose}>Відкрити у «Документи»</Button>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Наступний крок: роздрукувати, підписати і завантажити скан у «Документи».
                  </p>
                </div>
              )}

              {genState === "err" && (
                <div className="py-12 text-center">
                  <div className="mb-2 text-4xl">⛔</div>
                  <p className="font-semibold">Не вдалося створити документ</p>
                  <p className="mx-auto mt-1.5 mb-4 max-w-md text-xs text-muted-foreground">
                    {genError ?? "Помилка"} — нічого не збережено (атомарна генерація, orphan-файлів немає). Спробуйте ще раз.
                  </p>
                  <Button type="button" onClick={() => void doGenerate(false)} className="gap-1.5">
                    <RefreshCw className="h-3.5 w-3.5" /> Повторити
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* sticky footer */}
        <div className="flex flex-none items-center justify-between gap-2 border-t bg-card px-4 py-3 sm:px-6">
          <Button type="button" variant="outline" disabled={step === 1 || genState === "busy" || genState === "ok"}
            onClick={() => setStep((s) => Math.max(1, s - 1))}>
            ← Назад
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={tryClose} disabled={genState === "busy"}>
              {genState === "ok" ? "Закрити" : "Скасувати"}
            </Button>
            {genState !== "ok" && (
              <Button type="button" onClick={goNext}
                disabled={genState === "busy" || (blocked && step >= 2) || (step === 4 && (previewBusy || !preview))}>
                {step === 4 ? "⬇ Створити файл DOCX" : "Далі →"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* name-guard dialog — НЕ закривається Escape/кліком повз (E8) */}
      {gate === "pending_confirmation" && lookup?.nameGuard && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div role="alertdialog" aria-modal="true" className="w-full max-w-lg rounded-xl border bg-background p-5 shadow-2xl">
            <p className="mb-2.5 font-semibold">Реєстр повернув іншу назву</p>
            <div className="mb-2.5 grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 rounded-lg bg-muted p-3 text-[13px]">
              <span className="text-muted-foreground">Реєстр:</span>
              <b>{lookup.nameGuard.registryName ?? "—"}</b>
              <span className="text-muted-foreground">CRM:</span>
              <b>{lookup.nameGuard.crmName ?? "—"}</b>
            </div>
            <p className="mb-1.5 text-[13px] text-muted-foreground">
              Якщо це той самий клієнт (у CRM торгова назва) — підтвердіть: у документ підуть{" "}
              <b>юридичні назви з реєстру</b>, CRM-назва в документ не потрапляє. Підтвердження запишеться в аудит.
            </p>
            <p className="mb-4 text-[13px] text-muted-foreground">
              Якщо код виглядає чужим — відкрийте картку клієнта й перевірте код у 1С.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline"
                onClick={() => {
                  if (holdingId) window.open(`/clients/${holdingId}`, "_blank");
                  setLookup(null); // повертаємо крок у стан «до lookup» — чернетка полів зберігається
                }}>
                Відкрити картку клієнта
              </Button>
              <Button type="button" disabled={lookupBusy}
                onClick={() => void runLookup({ confirm: true })}>
                {lookupBusy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                Так, це той клієнт — підтверджую
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* diff-modal повторного lookup (§7.2) */}
      {diff && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div role="alertdialog" aria-modal="true" className="w-full max-w-lg rounded-xl border bg-background p-5 shadow-2xl">
            <p className="mb-2.5 font-semibold">Знайдено оновлення в реєстрі</p>
            {diff.map((d) => (
              <div key={d.key} className="mb-2">
                <p className="text-[13px] font-medium">{d.label}:</p>
                <div className="mt-1 grid grid-cols-[64px_1fr] gap-x-3 gap-y-1 rounded-lg bg-muted p-2.5 text-[13px]">
                  <span className="text-muted-foreground">у вас:</span>
                  <span>{d.mine} <i className="text-muted-foreground">(змінено вручну)</i></span>
                  <span className="text-muted-foreground">реєстр:</span>
                  <span>{d.registry}</span>
                </div>
              </div>
            ))}
            <p className="mb-4 text-[13px] text-muted-foreground">Ручні правки мовчки не затираються.</p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline"
                onClick={() => {
                  const r = pendingDiffLookup.current;
                  if (r) applyLookup(r, { keepMine: true });
                  setDiff(null);
                }}>
                Залишити моє
              </Button>
              <Button type="button"
                onClick={() => {
                  const r = pendingDiffLookup.current;
                  if (r) applyLookup(r);
                  setDiff(null);
                }}>
                Прийняти з реєстру
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* duplicate confirm-modal (Trap-64 / AC-13) */}
      {dupConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div role="alertdialog" aria-modal="true" className="w-full max-w-lg rounded-xl border bg-background p-5 shadow-2xl">
            <p className="mb-2.5 font-semibold">Додаткова угода №{dupConfirm.annexNumber} вже є в CRM</p>
            <p className="mb-4 text-[13px] text-muted-foreground">
              Створення ще одного файла з цим номером може призвести до плутанини. Сервер вимагає явного
              підтвердження (<code className="text-xs">confirmDuplicateAnnexNumber=true</code>).
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline"
                onClick={() => { setDupConfirm(null); setStep(1); }}>
                Повернутися і змінити номер
              </Button>
              <Button type="button"
                onClick={() => { setDupConfirm(null); void doGenerate(true); }}>
                Все одно створити
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* close confirm */}
      {closeConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div role="alertdialog" aria-modal="true" className="w-full max-w-sm rounded-xl border bg-background p-5 shadow-2xl">
            <p className="mb-4 text-sm"><b>Закрити майстер?</b><br />Введені дані буде втрачено.</p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCloseConfirm(false)}>Лишитись</Button>
              <Button type="button" variant="destructive" onClick={() => { setCloseConfirm(false); onClose(); }}>
                Закрити без збереження
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
